import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatAxiosError, fileUrl } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { FileText, Eye, Loader2, Upload } from "lucide-react";

const AUDIENCE_LABELS = { all: "Tous", pro: "Professionnels", particulier: "Particuliers" };

/** Conditions générales de vente : versions immuables, par public, pour toutes les propositions. */
export default function TermsSettings({ activeCompany, brand }) {
  const [versions, setVersions] = useState(null);
  const [form, setForm] = useState({ audience: "all", title: "", version_label: "", language: "fr" });
  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [preview, setPreview] = useState(null); // { id, title, format, content }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deactivatingId, setDeactivatingId] = useState(null);

  const load = () => api.get(`/companies/${activeCompany}/terms`).then((r) => setVersions(r.data)).catch(() => setVersions([]));
  useEffect(() => {
    if (activeCompany) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany]);

  const publish = async () => {
    if (!form.version_label.trim()) return toast.error("Le libellé de version est requis");
    if (!content.trim() && !file) return toast.error("Collez un texte ou choisissez un fichier");
    setPublishing(true);
    try {
      const fd = new FormData();
      fd.append("audience", form.audience);
      fd.append("language", form.language);
      if (form.title.trim()) fd.append("title", form.title.trim());
      fd.append("version_label", form.version_label.trim());
      if (file) fd.append("file", file);
      else fd.append("content", content);
      await api.post(`/companies/${activeCompany}/terms`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Version des CGV publiée");
      setForm({ audience: "all", title: "", version_label: "", language: "fr" });
      setContent("");
      setFile(null);
      load();
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setPublishing(false); }
  };

  const openPreview = async (id) => {
    setPreviewLoading(true);
    setPreview({ id });
    try {
      const { data } = await api.get(`/companies/${activeCompany}/terms/${id}`);
      setPreview(data);
    } catch (e) { toast.error(formatAxiosError(e)); setPreview(null); }
    finally { setPreviewLoading(false); }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Désactiver cette version des CGV ? Les nouvelles propositions n'y feront plus référence.")) return;
    setDeactivatingId(id);
    try {
      await api.post(`/companies/${activeCompany}/terms/${id}/deactivate`);
      toast.success("Version désactivée");
      load();
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setDeactivatingId(null); }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-6 mt-8" data-testid="terms-settings">
      <div className="flex items-center gap-2">
        <FileText className="w-5 h-5 text-primary" />
        <h3 className="font-heading text-lg font-semibold">Conditions générales (CGV) · {brand.name}</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        Présentées au client avant la signature de chaque proposition, pour toutes les sociétés. Une version
        publiée ne se modifie plus ; elle est acceptée et prouvée à chaque signature.
      </p>

      <div className="space-y-2">
        {versions === null ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
        ) : versions.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune version publiée pour l'instant.</p>
        ) : (
          versions.map((v) => (
            <div key={v.id} className="flex flex-wrap items-center justify-between gap-3 border border-border rounded-sm px-4 py-3" data-testid={`terms-version-${v.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate flex items-center gap-2">
                  {v.title || "CGV"}
                  {v.active && <span className="text-[10px] tracking-wide uppercase px-1.5 py-0.5 rounded-sm font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>}
                  {v.language === "en" && <span className="text-[10px] tracking-wide uppercase px-1.5 py-0.5 rounded-sm font-semibold bg-muted text-muted-foreground border border-border" data-testid={`terms-lang-${v.id}`}>Traduction EN</span>}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {AUDIENCE_LABELS[v.audience] || v.audience} · {v.version_label} · {new Date(v.created_at).toLocaleDateString("fr-FR")}
                  {v.format === "pdf" && " · PDF"}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {v.format === "pdf" ? (
                  <Button asChild variant="outline" size="sm" className="rounded-sm">
                    <a href={fileUrl(v.file_id)} target="_blank" rel="noopener noreferrer" data-testid={`terms-open-pdf-${v.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> Ouvrir le PDF
                    </a>
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => openPreview(v.id)} className="rounded-sm" data-testid={`terms-preview-${v.id}`}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> Aperçu
                  </Button>
                )}
                {v.active && (
                  <Button variant="outline" size="sm" onClick={() => deactivate(v.id)} disabled={deactivatingId === v.id}
                    className="rounded-sm text-destructive border-destructive/30 hover:bg-destructive/10" data-testid={`terms-deactivate-${v.id}`}>
                    {deactivatingId === v.id ? "…" : "Désactiver"}
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {preview && (
        <div className="border border-border rounded-sm overflow-hidden" data-testid="terms-preview-panel">
          <div className="flex items-center justify-between px-4 py-2.5 bg-muted/40 border-b border-border">
            <p className="text-sm font-medium">{preview.title || "Aperçu"}</p>
            <button onClick={() => setPreview(null)} className="text-xs text-muted-foreground hover:text-foreground">Fermer</button>
          </div>
          {previewLoading || preview.content == null ? (
            <div className="p-4 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
          ) : preview.format === "html" ? (
            <iframe title="Aperçu CGV" data-testid="terms-preview-iframe" srcDoc={preview.content} className="w-full h-96 bg-white" sandbox="" />
          ) : (
            <div className="p-4 text-sm whitespace-pre-wrap max-h-96 overflow-y-auto">{preview.content}</div>
          )}
        </div>
      )}

      <div className="border-t border-border pt-5 space-y-4">
        <p className="text-sm font-medium">Publier une nouvelle version</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Public</Label>
            <Select value={form.audience} onValueChange={(v) => setForm((f) => ({ ...f, audience: v }))}>
              <SelectTrigger className="mt-1.5 rounded-sm" data-testid="terms-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="pro">Professionnels</SelectItem>
                <SelectItem value="particulier">Particuliers</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Langue</Label>
            <Select value={form.language} onValueChange={(v) => setForm((f) => ({ ...f, language: v }))}>
              <SelectTrigger className="mt-1.5 rounded-sm" data-testid="terms-language"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">Français (version qui fait foi)</SelectItem>
                <SelectItem value="en">Anglais (traduction pour les clients anglophones)</SelectItem>
              </SelectContent>
            </Select>
            {form.language === "en" && (
              <p className="text-xs text-muted-foreground mt-1.5">Affichée en plus de la version française, qui reste la seule acceptée et prouvée.</p>
            )}
          </div>
          <div>
            <Label>Libellé de version</Label>
            <Input value={form.version_label} onChange={(e) => setForm((f) => ({ ...f, version_label: e.target.value }))}
              data-testid="terms-version-label" placeholder="Ex : v1 — septembre 2026" className="mt-1.5 rounded-sm" />
          </div>
        </div>
        <div>
          <Label>Titre (optionnel)</Label>
          <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            data-testid="terms-title" placeholder="Conditions générales de vente" className="mt-1.5 rounded-sm" />
        </div>
        <div>
          <Label>Texte des CGV</Label>
          <Textarea value={content} onChange={(e) => setContent(e.target.value)}
            disabled={!!file} rows={6} data-testid="terms-content" placeholder="Collez le texte des CGV…" className="mt-1.5 rounded-sm" />
          <p className="text-xs text-muted-foreground mt-1.5">Ou choisissez un fichier PDF, .txt ou .html ci-dessous — le texte collé sera alors ignoré. Un PDF est présenté tel quel au client et joint à son exemplaire signé.</p>
          <div className="mt-2 flex items-center gap-3">
            <input type="file" accept=".pdf,.txt,.html,application/pdf,text/plain,text/html" onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="terms-file-input" className="text-sm" />
            {file && <button type="button" onClick={() => setFile(null)} className="text-xs text-muted-foreground hover:text-destructive">Retirer le fichier</button>}
          </div>
        </div>
        <div className="pt-1">
          <Button onClick={publish} disabled={publishing} data-testid="terms-publish" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
            {publishing ? "Publication…" : <><Upload className="w-4 h-4 mr-1.5" /> Publier la version</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
